<template>
  <div class="contenedor-principal">
    <h1>Recursos y Descargas</h1>

    <p>Compartimos con vosotros una selección de materiales educativos que hemos ido creando con mucho cariño. Son recursos pensados para apoyar el aprendizaje en el aula, en casa o durante nuestras rutas. Si utilizáis alguno y os resulta útil, ¡nos encantaría saberlo!</p>

    <div class="filtros" role="group" aria-label="Filtrar recursos por tema">
      <button
        v-for="filtro in filtros"
        :key="filtro.tema"
        type="button"
        class="filtro"
        :class="{ activo: filtroActivo === filtro.tema }"
        :aria-pressed="filtroActivo === filtro.tema"
        @click="filtroActivo = filtro.tema"
      >
        {{ filtro.nombre }} ({{ filtro.total }})
      </button>
    </div>

    <div class="recursos-grid">
      <article v-for="recurso in recursosVisibles" :key="recurso.id" class="card">
        <div class="miniatura">
          <img :src="recurso.image" :alt="recurso.title" loading="lazy">
        </div>
        <span class="tipo">{{ tipos[recurso.tipo] }}</span>
        <h2>{{ recurso.title }}</h2>
        <div class="acciones">
          <component
            :is="accion.ruta ? 'router-link' : 'a'"
            v-for="accion in recurso.acciones"
            :key="accion.etiqueta"
            v-bind="enlaceAccion(accion)"
            class="accion"
            @click="registrarDescarga(recurso, accion)"
          >
            {{ accion.etiqueta }}
            <span v-if="accion.formato" class="formato">{{ accion.formato }}</span>
          </component>
        </div>
      </article>
    </div>

    <section class="franja-medida">
      <div>
        <h2>¿Necesitas algo a medida?</h2>
        <p>Podemos diseñar materiales personalizados para colegios, asociaciones o entidades locales: láminas, folletos o recursos digitales interactivos.</p>
      </div>
      <router-link to="/contacto?servicio=material_medida" class="btn-servicio">Pedir material a medida</router-link>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useHead } from '@vueuse/head'

const pageUrl = 'https://www.geobizi.com/servicios/descargas'
useHead({
  title: 'Recursos Didácticos y Descargas | Geobizi',
  meta: [{ name: 'description', content: 'Descarga láminas didácticas sobre clima, hongos, suelos, biodiversidad, geología y astronomía, en castellano y euskera, y guías interactivas de la naturaleza de Bizkaia.' }],
  link: [{ rel: 'canonical', href: pageUrl }]
})

const tipos = {
  guia: 'Guía interactiva',
  lamina: 'Lámina',
  documento: 'Documento',
  referencia: 'Referencia externa',
}

const temas = {
  clima: 'Clima',
  hongos: 'Hongos y suelo',
  biodiversidad: 'Flora y fauna',
  geologia: 'Geología',
  astronomia: 'Astronomía',
}

const LAMINAS = '/descargas/laminas/'
const MINIATURAS = '/descargas/miniaturas/'

// Para añadir un recurso basta con añadir una entrada: el filtro y su contador se actualizan solos.
// Cada acción lleva "archivo" (descarga), "ruta" (página de esta web) o "enlace" (otra web).
const recursos = [
  {
    id: 'lamina-acidificacion-oceanos',
    title: 'La acidificación de los océanos',
    tipo: 'lamina',
    tema: 'clima',
    image: `${MINIATURAS}acidificacion-oceanos.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}acidificacion-oceanos-es.pdf`, formato: 'PDF, 668 KB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}acidificacion-oceanos-eu.pdf`, formato: 'PDF, 644 KB' },
    ],
  },
  {
    id: 'lamina-ph-organismos',
    title: 'El pH y los organismos',
    tipo: 'lamina',
    tema: 'clima',
    image: `${MINIATURAS}ph-organismos.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}ph-organismos-es.pdf`, formato: 'PDF, 1 MB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}ph-organismos-eu.pdf`, formato: 'PDF, 1 MB' },
    ],
  },
  {
    id: 'lamina-temperatura-nubes',
    title: 'Temperatura y nubes',
    tipo: 'lamina',
    tema: 'clima',
    image: `${MINIATURAS}temperatura-nubes.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}temperatura-nubes-es.pdf`, formato: 'PDF, 749 KB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}temperatura-nubes-eu.pdf`, formato: 'PDF, 709 KB' },
    ],
  },
  {
    id: 'lamina-micelio',
    title: 'El micelio: el cuerpo oculto del hongo',
    tipo: 'lamina',
    tema: 'hongos',
    image: `${MINIATURAS}micelio.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}micelio-es.pdf`, formato: 'PDF, 430 KB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}micelio-eu.pdf`, formato: 'PDF, 429 KB' },
    ],
  },
  {
    id: 'lamina-reproduccion-hongos',
    title: 'Fases de la reproducción de los hongos',
    tipo: 'lamina',
    tema: 'hongos',
    image: `${MINIATURAS}reproduccion-hongos.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}reproduccion-hongos-es.pdf`, formato: 'PDF, 340 KB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}reproduccion-hongos-eu.pdf`, formato: 'PDF, 339 KB' },
    ],
  },
  {
    id: 'lamina-cultivo-setas',
    title: 'Cómo cultivar setas en un bote de cristal',
    tipo: 'lamina',
    tema: 'hongos',
    image: `${MINIATURAS}cultivo-setas.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}cultivo-setas-es.pdf`, formato: 'PDF, 859 KB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}cultivo-setas-eu.pdf`, formato: 'PDF, 853 KB' },
    ],
  },
  {
    id: 'lamina-tesoro-suelo',
    title: 'Un tesoro oculto: ¿qué hay debajo de nuestros pies?',
    tipo: 'lamina',
    tema: 'hongos',
    image: `${MINIATURAS}tesoro-suelo.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}tesoro-suelo-es.pdf`, formato: 'PDF, 827 KB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}tesoro-suelo-eu.pdf`, formato: 'PDF, 823 KB' },
    ],
  },
  {
    id: 'lamina-bombas-semillas',
    title: 'Bombas de semillas',
    tipo: 'lamina',
    tema: 'hongos',
    image: `${MINIATURAS}bombas-semillas.avif`,
    acciones: [
      { etiqueta: 'Castellano', archivo: `${LAMINAS}bombas-semillas-es.pdf`, formato: 'PDF, 2,5 MB' },
      { etiqueta: 'Euskera', archivo: `${LAMINAS}bombas-semillas-eu.pdf`, formato: 'PDF, 2,5 MB' },
    ],
  },
  {
    id: 'guia-etnobotanica',
    title: 'Fichas Etnobotánicas y Herbario de Euskal Herria',
    tipo: 'guia',
    tema: 'biodiversidad',
    image: '/imagenes/etnobotanica/catalogo-botanico.avif',
    acciones: [
      { etiqueta: 'Abrir guía', ruta: '/fitxas-etnobotanicas-enkarterri' },
    ],
  },
  {
    id: 'lamina-polinizadores',
    title: 'Insectos auxiliares y polinizadores',
    tipo: 'lamina',
    tema: 'biodiversidad',
    image: `${MINIATURAS}polinizadores.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}polinizadores.png`, formato: 'PNG, 1 MB' },
    ],
  },
  {
    id: 'lamina-mariposas-diurnas',
    title: 'Lámina de mariposas diurnas',
    tipo: 'lamina',
    tema: 'biodiversidad',
    image: `${MINIATURAS}mariposas.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}mariposas.png`, formato: 'PNG, 1,2 MB' },
    ],
  },
  {
    id: 'lamina-mariposas-nocturnas',
    title: 'Lámina de mariposas nocturnas',
    tipo: 'lamina',
    tema: 'biodiversidad',
    image: `${MINIATURAS}mariposas-nocturnas.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}mariposas-nocturnas.png`, formato: 'PNG, 1,2 MB' },
    ],
  },
  {
    id: 'lamina-arboles',
    title: 'Lámina de hojas de árboles (euskera y castellano)',
    tipo: 'lamina',
    tema: 'biodiversidad',
    image: `${MINIATURAS}arboles.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}arboles.png`, formato: 'PNG, 1,9 MB' },
    ],
  },
  {
    id: 'cartel-geologia',
    title: 'Cartel sobre las salidas de geología',
    tipo: 'lamina',
    tema: 'geologia',
    image: `${MINIATURAS}Cartel-geologia.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}cartel-geologia.png`, formato: 'PNG, 2,7 MB' },
    ],
  },
  {
    id: 'lamina-minerales-pigmentos',
    title: 'Minerales y pigmentos prehistóricos',
    tipo: 'lamina',
    tema: 'geologia',
    image: `${MINIATURAS}minerales-pigmentos.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}minerales-pigmentos.pdf`, formato: 'PDF, 676 KB' },
    ],
  },
  {
    id: 'documento-flysch',
    title: 'Documento didáctico sobre Flysch Bizkaia',
    tipo: 'documento',
    tema: 'geologia',
    image: `${MINIATURAS}flyschbizkaia-didactica.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: '/descargas/documentos/Flyschbizkaia-didactica.pdf', formato: 'PDF, 18 MB' },
    ],
  },
  {
    id: 'tabla-cronoestratigrafica',
    title: 'Tabla cronoestratigráfica internacional',
    tipo: 'referencia',
    tema: 'geologia',
    image: `${MINIATURAS}tabla-cronoestratigrafica.avif`,
    acciones: [
      { etiqueta: 'Ver en la fuente oficial', enlace: 'https://www2.sgc.gov.co/MGC/Documents/TCI2026-06/ChronostratChart2026-06SpanishAmer.pdf' },
    ],
  },
  {
    id: 'documento-eclipse',
    title: 'Mirando al cielo: el eclipse en La Galea',
    tipo: 'documento',
    tema: 'astronomia',
    image: `${MINIATURAS}eclipse-la-galea.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: '/descargas/documentos/eclipse-la-galea.pdf', formato: 'PDF, 2,7 MB' },
    ],
  },
  {
    id: 'lamina-eclipse-nodos',
    title: '¿Cuándo se produce un eclipse? Órbitas y nodos',
    tipo: 'lamina',
    tema: 'astronomia',
    image: `${MINIATURAS}eclipse-nodos.avif`,
    acciones: [
      { etiqueta: 'Descargar', archivo: `${LAMINAS}eclipse-nodos.png`, formato: 'PNG, 1,1 MB' },
    ],
  },
]

const filtroActivo = ref('todos')

const filtros = [
  { tema: 'todos', nombre: 'Todos', total: recursos.length },
  ...Object.entries(temas).map(([tema, nombre]) => ({
    tema,
    nombre,
    total: recursos.filter(r => r.tema === tema).length,
  })),
]

const recursosVisibles = computed(() =>
  filtroActivo.value === 'todos' ? recursos : recursos.filter(r => r.tema === filtroActivo.value)
)

const enlaceAccion = (accion) => {
  if (accion.ruta) return { to: accion.ruta }
  if (accion.enlace) return { href: accion.enlace, target: '_blank', rel: 'noopener noreferrer' }
  return { href: accion.archivo, download: '' }
}

// Función para registrar las descargas en Google Analytics
const registrarDescarga = (recurso, accion) => {
  if (accion.archivo && typeof window.gtag === 'function') {
    window.gtag('event', 'descargar_recurso', {
      'event_category': 'Descargas',
      'event_label': recurso.acciones.length > 1 ? `${recurso.id}-${accion.etiqueta}` : recurso.id
    });
  }
}
</script>

<style scoped>
.filtros {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1.5rem 1rem;
}

.filtro {
  padding: 0.4rem 0.9rem;
  background-color: var(--white);
  border: 1px solid var(--shoftgreen);
  border-radius: 1rem;
  color: var(--darkgreen);
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
}

.filtro:hover {
  background-color: var(--megashoftgreen);
}

.filtro.activo {
  background-color: var(--green);
  border-color: var(--green);
  color: var(--white);
}

.recursos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(200px, 100%), 1fr));
  gap: 1.25rem;
  margin: 0 1rem 2.5rem;
}

.card {
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  background-color: var(--megashoftgreen);
  border: 1px solid var(--shoftgreen);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Mismo tamaño para todas: las láminas se ven enteras, sin recortar */
.miniatura {
  position: relative;
  flex: none;
  aspect-ratio: 4 / 3;
  background-color: var(--white);
  border-radius: 0.5rem;
}

.miniatura img {
  position: absolute;
  inset: 0.25rem;
  width: calc(100% - 0.5rem);
  height: calc(100% - 0.5rem);
  object-fit: contain;
}

.tipo {
  margin-top: 0.75rem;
  color: var(--green);
  font-size: 0.75rem;
  font-weight: bold;
}

.card h2 {
  flex: 1;
  margin: 0.25rem 0 0.75rem;
  color: var(--darkgreen);
  font-size: 1rem;
  line-height: 1.3;
}

.acciones {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.accion {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0 0.5rem;
  padding: 0.35rem 0.6rem;
  background-color: var(--white);
  border: 1px solid var(--shoftgreen);
  border-radius: 4px;
  color: var(--darkgreen);
  font-size: 0.85rem;
  font-style: normal;
  transition: background-color 0.2s;
}

.accion:hover {
  background-color: var(--supershoftgreen);
  color: var(--darkgreen);
}

.formato {
  color: var(--darkgrey);
  font-weight: normal;
  white-space: nowrap;
}

.franja-medida {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin: 0 1rem;
  padding: 1.5rem 2rem;
  background-color: var(--supershoftgreen);
  border-radius: 0.5rem;
}

.franja-medida h2 {
  margin: 0 0 0.25rem;
  color: var(--darkgreen);
}

.franja-medida p {
  margin: 0;
}

.franja-medida .btn-servicio {
  flex-shrink: 0;
  font-style: normal;
}

@media (max-width: 750px) {
  .franja-medida {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .recursos-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }
}
</style>
